// Who may do what inside a project (SPEC-v3 2.2). The server always checks
// again; the web uses this to hide or disable controls and to say why, and
// the support assistant's why_not tool uses it to explain (docs/v3/ASSISTANT.md 3).
import type { OrgRole, ProjectAccessRole } from "./enums";

/** A person's access to a project (named ProjectRole in the web). */
export type PermissionRole = ProjectAccessRole;

/** Actions from the SPEC-v3 2.2 permission matrix. */
export type Action =
  | "read"
  | "edit" // activities, events, experts, stakeholders, ToR drafts, expenses...
  | "lead" // record ToR approval/changes, report submit/decision, invoices, amendments, contract, close, funder links
  | "admin" // privacy, data requests, join requests
  | "import" // stakeholder import, running-project import
  | "createProject"
  | "partners" // project partners and partner access
  | "log"; // activity log

const STAFF_EDIT: PermissionRole[] = ["owner", "admin", "lead", "member"];

export function can(role: PermissionRole | null | undefined, action: Action, opts: { isLead?: boolean } = {}): boolean {
  if (!role) return false;
  switch (action) {
    case "read":
      return true;
    case "edit":
    case "import":
      return STAFF_EDIT.includes(role);
    case "lead":
      return role === "lead" || opts.isLead === true;
    case "admin":
      return role === "owner" || role === "admin";
    case "partners":
      return role === "owner" || role === "admin" || role === "lead" || opts.isLead === true;
    case "log":
      return role !== "partner";
    case "createProject":
      return STAFF_EDIT.includes(role);
  }
}

/** Org-level: team pages, organisation settings. */
export function canOrg(role: OrgRole | null | undefined, action: "read" | "edit" | "invite" | "joinRequests"): boolean {
  if (!role) return false;
  if (action === "read") return true;
  return role === "owner" || role === "admin";
}

/** The sentence under a disabled control ("why can't I?"). */
export function whyNot(action: Action): string {
  switch (action) {
    case "lead":
      return "Only the contract lead can do this.";
    case "admin":
      return "Only organisation owners and admins can do this.";
    case "partners":
      return "Only the contract lead or an admin can change partner access.";
    default:
      return "Your role can read this page but not change it.";
  }
}

/** Every action, for the assistant's why_not tool. */
export const ACTIONS = ["read", "edit", "lead", "admin", "import", "createProject", "partners", "log"] as const satisfies readonly Action[];

/** What each action covers, in plain words. */
export const ACTION_LABELS: Record<Action, string> = {
  read: "Read this project",
  edit: "Edit activities, events, experts, stakeholders, ToR drafts and expenses",
  lead: "Record the funder's ToR decisions, submit reports, invoices and amendments, close the project",
  admin: "Privacy, data requests and join requests",
  import: "Import stakeholders or a running project",
  createProject: "Create a project",
  partners: "Manage project partners and their access",
  log: "See the activity log",
};
